// src/api/auth-api.js

// const API_URL = "https://ims.holdthechild.net";  // Use this for local development
const API_URL = "https://ims.holdthechild.net"; // Update with your actual API URL
export const getToken = () => localStorage.getItem("token");

export const loginUser = async (employeeEmail, password) => {
  try {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", 
      body: JSON.stringify({ email: employeeEmail, password }), 
    });

    const contentType = response.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Unexpected response format: ${text}`);
    }

    const data = await response.json();

    // --- Case 1: First-Time Login Password Change (202 Status) ---
    if (response.status === 202) {
         // The data object will contain { isFirstTimeLogin: true, email, message }
         return data;
    }
    
    // --- Case 2: Standard Error Handling (4xx, 5xx) ---
    if (!response.ok) {
        // Handle 401, 403, 404, etc.
        const message = data.message || data.msg || 'Login failed';
        throw new Error(message);
    }
    
    // --- Case 3: 200 OK Status (Should contain either final JWT or 2FA flags) ---
    // The backend's revised logic sends a 200 with twoFactorRequired: true 
    // to signal mandatory 2FA setup or verification.
    if (data.twoFactorRequired) {
        // Data will contain { twoFactorRequired, user:{is2FASetupComplete}, tempToken, message }
        return data; 
    }

    // --- Case 4: Standard Successful Login (Final JWT token) ---
    // This should theoretically only happen after a successful 2FA verification.
    return data;
    
  } catch (err) {
    console.error("Login error:", err.message);
    throw err; // Re-throw to be caught by the component
  }
};


export const registerUser = async (formData, token) => {
  const payload = {
    firstName: formData.firstName,
    lastName: formData.lastName,
    name: `${formData.firstName} ${formData.lastName}`,
    email: formData.email,
    role: formData.role,
    password: formData.oneTimePassword,
  };

  const response = await fetch(`${API_URL}/api/users/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.msg || "Failed to register user");
  }

  return data;
};


export const generate2FASecretForSetup = async (tempToken) => {
    try {
        const response = await fetch(`${API_URL}/api/auth/2fa/enable`, { // Adjusted path to include /api/
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                // 🔑 Use the short-lived token to secure the setup process
                'Authorization': `Bearer ${tempToken}`, 
            },
        });
        
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to generate 2FA secret.');
        }
        
        // This should return { qrCode, secret, message }
        return data; 
        
    } catch (error) {
        throw error;
    }
};


export const verify2FA = async (code, tempToken) => {
    try {
        const response = await fetch(`${API_URL}/api/auth/2fa/verify-code`, { // Adjusted path to include /api/
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                // 🔑 The temporary token is still crucial here
                'Authorization': `Bearer ${tempToken}`, 
            },
            body: JSON.stringify({ token: code }), // Note: backend expects 'token' for the 2FA code
        });
        
        const data = await response.json();

        if (!response.ok) {
            // The backend returns 401 on failure and 500 on server error
            throw new Error(data.message || '2FA verification failed.');
        }
        
        // This should return { token: JWT, user, message }
        return data;
        
    } catch (error) {
        throw error;
    }
};


export const resendEmailOTP = async (tempToken) => {
    try {
        // 💡 You need to create this route in your backend
        const response = await fetch(`${API_URL}/api/auth/resend-email-otp`, { 
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${tempToken}`, 
            },
        });
        
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to resend email code.');
        }
        
        return data; 
        
    } catch (error) {
        throw error;
    }
};


export const verifyEmailOTP = async (code, tempToken) => {
    try {
        const response = await fetch(`${API_URL}/api/auth/verify-email-otp`, { 
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${tempToken}`, 
            },
            body: JSON.stringify({ code }), // Backend expects 'code'
        });
        
        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Email verification failed.');
        }
        
        // 🔑 CRITICAL CHANGE FOR 2FA FLOW: 
        // Backend now returns the final JWT token here.
        // The data object should now contain: { token: JWT, user: {...}, message }
        return data;
        
    } catch (error) {
        throw error;
    }
};